import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import {
    getCurrentPage,
    getLocation,
    removeAbsoluteUrl,
} from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SignInChangePasswordForm from "@insite/content-library/Widgets/SignIn/SignInChangePasswordForm";
import SignInResetPasswordForm from "@insite/content-library/Widgets/SignIn/SignInResetPasswordForm";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import { FormFieldStyle, FormInputWrapper } from "@insite/mobius/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconProps, IconWrapper } from "@insite/mobius/Icon/Icon";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    changePasswordInstructions = "changePasswordInstructions",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.changePasswordInstructions]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const parsedQuery = parseQueryString<{ externalError?: string }>(getLocation(state).search);
    const pageType = getCurrentPage(state).type;
    const isSignInPage = pageType === "SignInPage";
    const isConfirmationPage = pageType === "OrderConfirmationPage" || pageType === "OnePageCheckoutConfirmationPage";
    return {
        accountSettings: getSettingsCollection(state).accountSettings,
        externalError: parsedQuery.externalError,
        isConfirmationPage,
        homePageUrl: getHomePageUrl(state),
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapDispatchToProps = {
    signIn,
};

export interface SignInExistingAccountStyles {
    wrapper?: InjectableCss;
    form?: InjectableCss;
    rememberMeCheckbox?: CheckboxProps;
    rememberMeCheckboxGroup?: CheckboxGroupComponentProps;
    userNameTextField?: TextFieldProps;
    signInGridContainer?: GridContainerProps;
    icon?: IconProps;
    passwordTextField?: TextFieldProps;
    signInButton?: ButtonPresentationProps;
    signInButtonGridItem?: GridItemProps;
    forgotPasswordGridItem?: GridItemProps;
    rememberMeGridItem?: GridItemProps;
    passwordGridItem?: GridItemProps;
    userNameGridItem?: GridItemProps;
    signInExistingAccountTitle?: TypographyProps;
    forgotPasswordLink?: LinkPresentationProps;
    resetPasswordModal?: ModalPresentationProps;
    changePasswordModal?: ModalPresentationProps;
}

export const signInExistingAccountStyles: SignInExistingAccountStyles = {
    wrapper: {
        css: css`
            background: ${getColor("info.contrast")};
            padding: 30px;
            margin: 0;
            min-height: 345px;
        `,
    },
    icon: {
        css: css`
            padding-top: 50px;
        `,
    },
    signInExistingAccountTitle: {
        variant: "h3",
        css: css`
            color: ${getColor("secondary")};
        `,
    },
    userNameGridItem: {
        width: 12,
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding-bottom: 10px;
            > ${FormFieldStyle} {
                flex-direction: row;
                > ${TypographyStyle} {
                    display: flex;
                    align-items: center;
                    color: ${getColor("secondary")};
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                width: 40%;
                            `,
                            css`
                                width: 40%;
                            `,
                            css`
                                width: 30%;
                            `,
                            css`
                                width: 30%;
                            `,
                            css`
                                width: 30%;
                            `,
                        ])}
                }
                > ${FormInputWrapper} {
                    width: 70%;
                    margin: 0px;
                }
            }
        `,
    },
    rememberMeCheckbox: {
        css: css`
            > ${IconWrapper} {
                background: ${getColor("primary.contrast")};
            }
            > ${TypographyStyle} {
                color: ${getColor("secondary")};
            }
        `,
    },
    passwordGridItem: {
        width: 12,
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            > ${FormFieldStyle} {
                flex-direction: row;
                > ${TypographyStyle} {
                    display: flex;
                    align-items: center;
                    color: ${getColor("secondary")};
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                width: 40%;
                            `,
                            css`
                                width: 40%;
                            `,
                            css`
                                width: 30%;
                            `,
                            css`
                                width: 30%;
                            `,
                            css`
                                width: 30%;
                            `,
                        ])}
                }
                > ${FormInputWrapper} {
                    width: 70%;
                    margin: 0px;
                }
            }
        `,
    },
    signInButtonGridItem: {
        width: 12,
        css: css`
            justify-content: flex-start;
            padding-top: 7px;
        `,
    },
    rememberMeGridItem: {
        width: 7,
        css: css`
            padding-top: 5px;
            padding-bottom: 0px;
        `,
    },
    forgotPasswordGridItem: {
        width: 5,
        css: css`
            justify-content: flex-end;
        `,
    },
    forgotPasswordLink: {
        typographyProps: {
            size: 14,
        },
    },
    signInButton: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    resetPasswordModal: {
        sizeVariant: "small",
        cssOverrides: {
            modalTitle: css`
                > ${TypographyStyle} {
                    color: ${getColor("secondary")};
                }
            `,
        },
    },
    changePasswordModal: {
        sizeVariant: "small",
    },
};

/**
 * @deprecated Use signInExistingAccountStyles instead.
 */
export const signInExistingAccount = signInExistingAccountStyles;
const styles = signInExistingAccountStyles;
const StyledForm = getStyledWrapper("form");

const SignInExistingAccount = ({
    signIn,
    fields,
    accountSettings,
    externalError,
    isConfirmationPage,
    homePageUrl,
}: Props) => {
    const [rememberMe, setRememberMe] = useState(false);
    const [keepMeSignedIn, setKeepMeSignedIn] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [userName, setUserName] = useState("");
    const [password, setPassword] = useState("");
    const [isSignInClicked, setIsSignInClicked] = useState(false);
    const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
    const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState(externalError || "");

    const showRememberMe = accountSettings.rememberMe;
    const showKeepMeSignedIn = accountSettings.keepMeSignedIn;

    const signInHandler = (newPassword?: string) => {
        const pass = newPassword || password;
        setIsSignInClicked(true);
        if (!userName || !pass) {
            return;
        }

        if (accountSettings.useEmailAsUserName && !validateEmail(userName)) {
            return;
        }

        const queryParams = parseQueryString<{ returnUrl?: string }>(window.location.search);
        const returnUrl = removeAbsoluteUrl(queryParams.returnUrl);

        setErrorMessage("");

        signIn({
            userName,
            password: pass,
            rememberMe,
            keepMeSignedIn,
            returnUrl,
            onError: (error, statusCode) => {
                if (statusCode === 422) {
                    setIsChangePasswordModalOpen(true);
                    return;
                }

                setErrorMessage(error);
            },
        });
    };

    const submitHandler = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        signInHandler();
    };

    const onResetPasswordClose = () => {
        setIsResetPasswordModalOpen(false);
    };

    const onChangePasswordClose = () => {
        setIsChangePasswordModalOpen(false);
    };

    const onPasswordChanged = (newPassword: string) => {
        setPassword(newPassword);
        setIsChangePasswordModalOpen(false);
        signInHandler(newPassword);
    };

    const validateUserName = () => {
        if (isSignInClicked) {
            if (userName) {
                if (accountSettings.useEmailAsUserName && !validateEmail(userName)) {
                    return siteMessage("SignInInfo_Email_Validation");
                }
            } else {
                return accountSettings.useEmailAsUserName
                    ? siteMessage("SignInInfo_Email_Required")
                    : siteMessage("SignInInfo_UserName_Required");
            }
        }

        return "";
    };

    const userErrorMessage = validateUserName();
    const passwordErrorMessage =
        !password && isSignInClicked ? siteMessage("SignInInfo_Password_Required") : errorMessage || "";

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.signInExistingAccountTitle}>{translate("Already have an account?")}</Typography>
            <StyledForm {...styles.form} onSubmit={submitHandler} noValidate>
                <GridContainer {...styles.signInGridContainer}>
                    <GridItem {...styles.userNameGridItem}>
                        <TextField
                            id="userName"
                            {...styles.userNameTextField}
                            label={accountSettings.useEmailAsUserName ? translate("Email") : translate("User Name")}
                            onChange={e => setUserName(e.currentTarget.value)}
                            value={userName}
                            error={userErrorMessage}
                            autoComplete="username"
                            data-test-selector="signIn_userName"
                            placeholder="Email"
                            required
                        />
                    </GridItem>
                    <GridItem {...styles.passwordGridItem}>
                        <TextField
                            id="password"
                            {...styles.passwordTextField}
                            label={translate("Password")}
                            onChange={e => setPassword(e.currentTarget.value)}
                            value={password}
                            error={passwordErrorMessage}
                            placeholder="Password"
                            iconProps={{
                                ...styles.icon,
                                src: showPassword ? EyeOff : Eye,
                            }}
                            iconClickableProps={{
                                onClick: () => {
                                    setShowPassword(!showPassword);
                                },
                                type: "button",
                            }}
                            clickableText={password}
                            autoComplete="current-password"
                            data-test-selector="signIn_password"
                            type={showPassword ? "text" : "password"}
                            required
                        />
                    </GridItem>
                    <GridItem {...styles.rememberMeGridItem}>
                        {showRememberMe && (
                            <CheckboxGroup {...styles.rememberMeCheckboxGroup}>
                                <Checkbox
                                    uid="rememberMe"
                                    {...styles.rememberMeCheckbox}
                                    checked={rememberMe}
                                    onChange={(_, value) => setRememberMe(value)}
                                >
                                    {translate("Remember Me")}
                                </Checkbox>
                            </CheckboxGroup>
                        )}
                        {showKeepMeSignedIn && (
                            <CheckboxGroup {...styles.rememberMeCheckboxGroup}>
                                <Checkbox
                                    uid="keepMeSignedIn"
                                    {...styles.rememberMeCheckbox}
                                    checked={keepMeSignedIn}
                                    onChange={(_, value) => setKeepMeSignedIn(value)}
                                >
                                    {translate("Keep Me Signed In")}
                                </Checkbox>
                            </CheckboxGroup>
                        )}
                    </GridItem>
                    <GridItem {...styles.forgotPasswordGridItem}>
                        <Link
                            {...styles.forgotPasswordLink}
                            type="button"
                            onClick={() => setIsResetPasswordModalOpen(true)}
                            data-test-selector="signIn_forgotPassword"
                        >
                            {translate("Forgot Password?")}
                        </Link>
                        <Modal
                            headline={translate("Reset Password")}
                            {...styles.resetPasswordModal}
                            isOpen={isResetPasswordModalOpen}
                            handleClose={() => onResetPasswordClose()}
                            data-test-selector="resetPasswordModal"
                        >
                            <SignInResetPasswordForm onClose={() => onResetPasswordClose()} />
                        </Modal>
                    </GridItem>
                    <GridItem {...styles.signInButtonGridItem}>
                        <Button {...styles.signInButton} type="submit" data-test-selector="signIn_submit">
                            {translate("Sign In")}
                        </Button>
                        <Modal
                            headline={translate("Change Password")}
                            data-test-selector="changePasswordModal"
                            {...styles.changePasswordModal}
                            isOpen={isChangePasswordModalOpen}
                            handleClose={onChangePasswordClose}
                        >
                            <SignInChangePasswordForm
                                enteredUserName={userName}
                                enteredPassword={password}
                                instructionsText={fields.changePasswordInstructions}
                                onPasswordChanged={onPasswordChanged}
                            />
                        </Modal>
                    </GridItem>
                </GridContainer>
            </StyledForm>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(SignInExistingAccount),
    definition: {
        allowedContexts: ["SignInPage"],
        group: "Sign In",
        icon: "LogIn",
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.changePasswordInstructions,
                editorTemplate: "RichTextField",
                displayName: "Change Password Instructions",
                defaultValue: "A password change is required for your account before you may continue.",
                extendedConfig: { height: 170 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
            },
        ],
    },
};

export default widgetModule;
